<template>
  <div class="table-dropdown-v2">
    <div v-if="showFilter" class="table-dropdown-v2__section">
      <div class="table-dropdown-v2__label">{{ filterLabel }}</div>
      <search-input
        size="medium"
        class="table-dropdown-v2__search"
        :value="filterQuery"
        :placeholder="searchPlaceholder"
        :search-label="$t('TABLE_V2.SEARCH_BUTTON')"
        @search="val => $emit('update:filterQuery', val)"
      />
    </div>

    <div
      v-if="showFilter && filterOptions.length"
      class="table-dropdown-v2__divider"
    />

    <div v-if="filterOptions.length" class="table-dropdown-v2__section">
      <div
        v-for="option in filterOptions"
        :key="option.value"
        class="table-dropdown-v2__filter-option"
        @click="$emit('toggle-filter', option.value)"
      >
        <span
          class="table-dropdown-v2__radio"
          :class="{
            'table-dropdown-v2__radio--checked': selectedFilters.includes(
              option.value
            ),
          }"
        />
        <span class="table-dropdown-v2__filter-text">{{ option.label }}</span>
      </div>
    </div>

    <div v-if="showSort" class="table-dropdown-v2__section">
      <div class="table-dropdown-v2__label">{{ sortLabel }}</div>
      <div
        v-for="option in sortOptions"
        :key="option.value"
        class="table-dropdown-v2__sort-option"
        :class="{
          'table-dropdown-v2__sort-option--active':
            option.value === currentSort,
        }"
        @click="$emit('sort', option.value)"
      >
        <span class="table-dropdown-v2__sort-icon">
          <v2-icon
            icon="swap_vert"
            size="16"
            :class="{ '-scale-x-100': option.value === 'desc' }"
          />
        </span>
        <span class="table-dropdown-v2__sort-text">{{ option.label }}</span>
        <span
          v-if="option.value === currentSort"
          class="table-dropdown-v2__sort-check"
        >
          <v2-icon icon="check" size="12" class="text-menu-active-icon" />
        </span>
      </div>
    </div>

    <slot name="filter-options" />
  </div>
</template>

<script>
import V2Icon from 'v2/components/Icon.vue';
import SearchInput from 'v2/components/SearchInput.vue';

export default {
  name: 'TableDropdownV2',
  components: { V2Icon, SearchInput },
  props: {
    showFilter: {
      type: Boolean,
      default: true,
    },
    filterLabel: {
      type: String,
      default: 'Filter by',
    },
    sortLabel: {
      type: String,
      default: 'Sort by',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...',
    },
    filterQuery: {
      type: String,
      default: '',
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    currentSort: {
      type: String,
      default: '',
    },
    filterOptions: {
      type: Array,
      default: () => [],
    },
    selectedFilters: {
      type: Array,
      default: () => [],
    },
    showSort: {
      type: Boolean,
      default: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.table-dropdown-v2__section {
  padding: 0;
}

.table-dropdown-v2__label {
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: theme('colors.gray.400');
  text-transform: none;
  padding: 4px 8px;
}

.table-dropdown-v2 .table-dropdown-v2__search {
  position: relative;
  width: auto;
  min-width: 171px;
  margin: 4px 8px;
  height: 36px;
  border-radius: 8px;
  padding-block: 6px;
  padding-inline-start: 8px;
  padding-inline-end: 4px;

  ::v-deep .search-input__field {
    gap: 4px;
    height: 100%;
  }

  ::v-deep .search-input__control {
    width: 0;
    font-size: 14px;
    line-height: 20px;
    height: 100%;
    min-height: 0;
  }

  ::v-deep .search-input__submit {
    display: none;
  }
}

.table-dropdown-v2__search-icon {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
  color: theme('colors.gray.500');
}

.table-dropdown-v2__search-input {
  width: 100%;
  height: 36px;
  padding: 0 28px 0 40px;
  border: 1px solid theme('colors.gray.300');
  border-radius: 8px;
  font-size: 14px;
  color: theme('colors.gray.700');
  background: theme('colors.white');
  outline: none;
  margin: 0;
  box-sizing: border-box;

  &::placeholder {
    color: theme('colors.gray.400');
  }

  &:focus {
    border-color: theme('colors.brand.DEFAULT');
  }
}

.table-dropdown-v2__search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: theme('colors.gray.400');
  font-size: 16px;
  line-height: 1;

  &:hover {
    color: theme('colors.gray.700');
  }
}

.table-dropdown-v2__divider {
  height: 1px;
  background: theme('colors.gray.200');
  margin: 12px 8px 8px;
}

.table-dropdown-v2__sort-option {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  color: theme('colors.gray.600');
  text-transform: none;
  font-weight: 400;

  &:hover {
    background: theme('colors.gray.100');
  }

  &--active {
    background: theme('colors.green.50');
    color: theme('colors.menu-active.text');

    .table-dropdown-v2__sort-icon {
      color: theme('colors.menu-active.icon');
    }
  }
}

.table-dropdown-v2__sort-icon {
  display: flex;
  align-items: center;
  color: theme('colors.gray.500');
}

.table-dropdown-v2__sort-text {
  flex: 1;
}

.table-dropdown-v2__sort-check {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.table-dropdown-v2__filter-option {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  color: theme('colors.gray.600');
  text-transform: none;
  font-weight: 400;

  &:hover {
    background: theme('colors.gray.100');
  }
}

.table-dropdown-v2__radio {
  position: relative;
  width: 18px;
  height: 18px;
  margin: 3px;
  flex-shrink: 0;
  border: 1px solid theme('colors.gray.500');
  border-radius: 50%;
  background: theme('colors.white');
  box-sizing: border-box;
  transition: border-color 0.15s;

  &--checked {
    border-color: theme('colors.brand.DEFAULT');

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 12px;
      height: 12px;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background: theme('colors.brand.DEFAULT');
    }
  }
}

.table-dropdown-v2__filter-text {
  flex: 1;
}
</style>
